import React from 'react';
import type { DialogProps } from '@archipro-design/aria';
import { Caption } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Flex } from '@archipro-design/aria';
import {
    useStyles,
    Dialog,
    pxToRem,
    pxArrayToRem,
    Button,
    Subheader,
} from '@archipro-design/aria';
import * as S from './ShippingPopup.style';
import { CloseLine } from '@archipro-design/icons';
import ShippingForm from './ShippingForm';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import type { ItemShippingType } from '../../context/ItemShippingContext';
import { useTracker } from '@archipro-website/tracker';

interface Props extends Omit<DialogProps, 'content' | 'onConfirm'> {
    isSample: boolean;
    confirmButtonText?: string;
    minButtonWidth?: number;
    loading?: boolean;
    onConfirm?: (shippingMethod: ItemShippingType | null) => void;
}

const ShippingPopup = ({
    variables,
    onCancel,
    onConfirm,
    isSample,
    confirmButtonText = 'Confirm',
    minButtonWidth = 158,
    loading,
    ...rest
}: Props) => {
    const { css } = useStyles();
    const { shipping, setShipping } = useVariantShipping(isSample);
    const [formBusy, setFormBusy] = React.useState(false);

    const [draftShipping, setDraftShipping] = React.useState(shipping);
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();

    const HeaderText = mobile ? Caption : Subheader;

    React.useEffect(() => {
        setDraftShipping(shipping);
    }, [shipping]);

    const onConfirmClick = () => {
        setShipping(draftShipping);
        onConfirm?.(draftShipping);
    };

    const onClosing: DialogProps['onCancel'] = (e, props) => {
        if (loading || formBusy) {
            return;
        }
        onCancel?.(e, props);
    };

    return (
        <Dialog
            className={css(S.ShippingPopup)}
            content={{
                children: (
                    <div className={css(S.PopupContent)}>
                        <Flex
                            className={css(S.PopupHeader)}
                            vAlign="center"
                            space="between"
                        >
                            <HeaderText variant="03" className={css(S.Title)}>
                                {`Select ${isSample ? 'sample' : ''} shipping
                                method`}
                            </HeaderText>
                            <Button
                                className={css(S.BackButton)}
                                icon={<CloseLine />}
                                text
                                transparent
                                onClick={onCancel}
                                size={mobile ? 24 : 30}
                                variables={{
                                    buttonPadding: pxToRem(8),
                                    iconPadding: 0,
                                }}
                            />
                        </Flex>
                        <ShippingForm
                            shipping={draftShipping}
                            onChange={(shipping: ItemShippingType) => {
                                tracker.log('ShippingOptionSelect', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData: shipping.ID,
                                    },
                                });
                                setDraftShipping?.(shipping);
                            }}
                            isSample={isSample}
                            onFetchStatusChange={setFormBusy}
                        />
                    </div>
                ),
            }}
            confirmButton={{
                children: () => (
                    <Button
                        color={'primary'}
                        className={css(S.ConfirmButton)}
                        onClick={onConfirmClick}
                        size={mobile ? 16 : 20}
                        loading={loading}
                        disabled={formBusy}
                        fluid={mobile}
                        variables={{
                            buttonMinWidth: pxToRem(minButtonWidth),
                            buttonPadding: pxArrayToRem([12, 32]),
                            buttonSize20LineHeight: pxToRem(26),
                        }}
                    >
                        {confirmButtonText}
                    </Button>
                ),
            }}
            variables={{
                rootBorderRadius: 0,
                rootPadding: mobile
                    ? pxToRem(12)
                    : pxArrayToRem([48, 0, 32, 48]),
                rootWidth: mobile ? '100vw' : pxToRem(800),
                contentMargin: 0,
                ...variables,
            }}
            onCancel={onClosing}
            {...rest}
        />
    );
};

export default ShippingPopup;
